import {
  AnnotationsIntegrationMarkerConfig,
  AnnotationsIntegrationTrackConfig,
  IndicatorData,
} from '../../../../core/components/annotations/components/indicators/types.js';
import {
  buildIndicator,
  isAlwaysHidden,
  isAlwaysShown,
} from '../../../../core/components/annotations/components/indicators/utils/indicator-visibility-helpers.js';
import { DEFAULT_MARKER_COLOR } from '../../../../core/components/annotations/constants.js';
import { AnnotationsTrackConfig } from '../../../../core/components/annotations/types/annotations-track-props.js';
import { SelectionState } from '../../../../core/components/annotations/types/state.js';
import { getMinMaxAsNumber } from '../../../../core/components/annotations/utils/get-min-max-as-number.js';
import { populateMissingMarkerProps } from '../../../../core/components/annotations/utils/populate-missing-marker-props.js';
import { populateMissingTrackProps } from '../../../../core/components/annotations/utils/populate-missing-track-props.js';
import { serializeMarkerRange } from '../../../../core/components/annotations/utils/serialize-marker-range.js';
import { getDatumValue } from '../../../processing-strategies/utils/access-datapoint/get-datapoints.js';
import { XYChartAnnotationsMarkerProps } from '../../../slots/Annotations/Marker.js';
import { XYChartAnnotationsTrackProps } from '../../../slots/Annotations/Track.js';
import { XYChartData } from '../../../types/xy-chart.js';
import { evaluateOptionalAttribute } from '../../../utils/evaluate-with-data.js';

const serializeAnnotationsMarkerFromXYChartData = (
  data: XYChartData,
  markerConfig: XYChartAnnotationsMarkerProps,
): AnnotationsIntegrationMarkerConfig => {
  const response = {
    ...data,
    start: getDatumValue(data, markerConfig.startAccessor),
    end: getDatumValue(data, markerConfig.endAccessor),
    symbol: evaluateOptionalAttribute(markerConfig.symbol, data, undefined),
    hidden: evaluateOptionalAttribute(markerConfig.hidden, data, false),
    priority: evaluateOptionalAttribute(markerConfig.priority, data, 0),
    color: markerConfig.colorAccessor
      ? (getDatumValue(data, markerConfig.colorAccessor) as string)
      : undefined,
    title: markerConfig.titleAccessor
      ? (getDatumValue(data, markerConfig.titleAccessor) as string)
      : undefined,
    description: markerConfig.descriptionAccessor
      ? (getDatumValue(data, markerConfig.descriptionAccessor) as string)
      : undefined,
    indicatorsDisplay: evaluateOptionalAttribute(
      markerConfig.indicatorsDisplay,
      data,
      undefined,
    ),
  };

  return response as AnnotationsIntegrationMarkerConfig;
};

const serializeAnnotationsTrackFromXYChartData = (
  data: XYChartData,
  trackConfig: XYChartAnnotationsTrackProps,
): AnnotationsIntegrationTrackConfig => {
  return {
    hidden: evaluateOptionalAttribute(trackConfig.hidden, data, false),
    symbol: evaluateOptionalAttribute(trackConfig.symbol, data, undefined),
    color: trackConfig.colorAccessor
      ? (getDatumValue(data, trackConfig.colorAccessor) as string)
      : undefined,
    label: trackConfig.labelAccessor
      ? (getDatumValue(data, trackConfig.labelAccessor) as string)
      : undefined,
    indicatorsDisplay: evaluateOptionalAttribute(
      trackConfig.indicatorsDisplay,
      data,
      'auto',
    ),
    markers: [],
  };
};

export const getIndicatorsFromAnnotations = (
  data: XYChartData[],
  tracksConfig: XYChartAnnotationsTrackProps,
  markersConfig: XYChartAnnotationsMarkerProps,
  selection: SelectionState,
) => {
  const alwaysVisible: IndicatorData[] = [];
  const selectable: Map<string, IndicatorData> = new Map();
  const trackIdTroughAccesor = tracksConfig.trackIdAccessor;
  const hiddenTracks = new Set<string>();
  const annotationsByTrack: Map<string, AnnotationsTrackConfig> = data.reduce(
    (trackMap, datum) => {
      const trackId = getDatumValue(datum, trackIdTroughAccesor) as string;
      const annotationsTrack: AnnotationsIntegrationTrackConfig = trackMap.has(
        trackId,
      )
        ? trackMap.get(trackId)
        : populateMissingTrackProps(
            serializeAnnotationsTrackFromXYChartData(datum, tracksConfig),
          );
      if (annotationsTrack?.hidden) {
        hiddenTracks.add(trackId);
      }

      const marker = serializeAnnotationsMarkerFromXYChartData(
        datum,
        markersConfig,
      );

      const populatedMarker: AnnotationsIntegrationMarkerConfig =
        populateMissingMarkerProps(marker, annotationsTrack);

      annotationsTrack.markers.push(populatedMarker);
      trackMap.set(trackId, annotationsTrack);

      const { color: trackColor, indicatorsDisplay: trackIndicatorDisplay } =
        annotationsTrack;
      const {
        color: markerColor,
        start: markerStartRaw,
        end: markerEndRaw,
        indicatorsDisplay: markerIndicatorDisplay,
        priority,
      } = populatedMarker;

      const { min: markerStart, max: markerEnd } = getMinMaxAsNumber(
        markerStartRaw,
        markerEndRaw,
      );

      if (!isAlwaysHidden(markerIndicatorDisplay, trackIndicatorDisplay)) {
        const trackIndex = Array.from(trackMap.keys()).findIndex(
          (name) => name === trackId,
        );
        const markerId = serializeMarkerRange(
          { ...populatedMarker, start: markerStart, end: markerEnd },
          trackIndex,
        );
        const color = markerColor ?? trackColor ?? DEFAULT_MARKER_COLOR;
        const indicator = buildIndicator(
          color,
          selection,
          markerId,
          markerStart,
          markerEnd,
          priority,
        );

        if (isAlwaysShown(markerIndicatorDisplay, trackIndicatorDisplay)) {
          alwaysVisible.push(indicator);
        } else {
          selectable.set(markerId, indicator);
        }
      }
      return trackMap;
    },
    new Map(),
  );
  return {
    indicators: { alwaysVisible, selectable },
    annotationsByTrack,
    hiddenTracks,
  };
};
